<template>
  <div
    class="month-picker__container"
    :class="{
      [`month-picker--${variant}`]: true,
      'year-picker': yearOnly
    }"
  >
    <div v-if="showYear" class="month-picker__year">
      <button type="button" @click="changeYear(-1)">&lsaquo;</button>
      <p v-if="!editableYear">
        {{ year }}
      </p>
      <input v-else v-model.number="year" type="text" @change="onChange()" />
      <button type="button" @click="changeYear(+1)">&rsaquo;</button>
    </div>
    <div v-if="!yearOnly" class="month-picker">
      <div
        v-for="(month, monthIndex) in monthsByLang"
        :key="month"
        :class="{
          inactive: isInactive(month),
          clearable: clearable,
          selected:
            (highlightExactDate &&
              !range &&
              showYear &&
              currentMonthIndex === monthIndex &&
              year === selectedYear) ||
            (!range && !showYear && currentMonthIndex == monthIndex) ||
            (!highlightExactDate && !range && currentMonthIndex === monthIndex),
          'selected-range':
            range &&
            monthIndex > firstRangeMonthIndex &&
            monthIndex < secondRangeMonthIndex,
          'selected-range-first': range && firstRangeMonthIndex === monthIndex,
          'selected-range-second': range && secondRangeMonthIndex === monthIndex
        }"
        class="month-picker__month"
        @click="selectMonth(monthIndex, true)"
      >
        {{ month }}
      </div>
    </div>
  </div>
</template>

<script>

import monthPicker from "./month-picker";

export default {
  name: "MonthPicker",
  mixins: [monthPicker],
  emits: ["change", "clear", "input", "change-year"],
  data: () => ({
    currentMonthIndex: null,
    firstRangeMonthIndex: null,
    secondRangeMonthIndex: null,
    year: new Date().getFullYear(),
    selectedYear: new Date().getFullYear()
  }),
  computed: {
    currentMonth: function() {
      if (this.currentMonthIndex !== null) {
        return this.monthsByLang[this.currentMonthIndex];
      }

      return null;
    },
    firstRangemonth: function() {
      if (this.firstRangeMonthIndex !== null) {
        return this.monthsByLang[this.firstRangeMonthIndex];
      }

      return null;
    },
    secondRangemonth: function() {
      if (this.secondRangeMonthIndex !== null) {
        return this.monthsByLang[this.secondRangeMonthIndex];
      }

      return null;
    },
    date: function() {
      const month = this.monthsByLang.indexOf(this.currentMonth) + 1;
      let dateFrom = new Date(`${this.year}/${month}/01`);
      let dateTo = new Date(this.year, month, 1);

      const dateResult = {
        from: dateFrom,
        to: dateTo,
        month: this.monthsByLang[month - 1],
        monthIndex: month,
        year: this.year,
        selectedYear: this.selectedYear,
        rangeFrom: null,
        rangeTo: null,
        rangeFromMonth: null,
        rangeToMonth: null
      };

      if (this.range) {
        const monthRangeFrom =
          this.monthsByLang.indexOf(this.firstRangeMonthIndex) + 1;
        const monthRangeTo =
          this.monthsByLang.indexOf(this.secondRangeMonthIndex) + 1;

        dateFrom = new Date(`${this.year}/${monthRangeFrom}/01`);
        dateTo = new Date(`${this.year}/${monthRangeTo}/01`);

        dateResult.rangeFrom = this.firstRangeMonthIndex;
        dateResult.rangeTo = this.secondRangeMonthIndex;
        dateResult.rangeFromMonth = this.monthsByLang[
          this.firstRangeMonthIndex
        ];
        dateResult.rangeToMonth = this.monthsByLang[this.secondRangeMonthIndex];
      }

      return dateResult;
    }
  },
  watch: {
    defaultMonth(newVal) {
      this.currentMonthIndex = newVal - 1;
    },
    defaultYear(newVal) {
      this.year = newVal;
      this.selectedYear = newVal;
    }
  },
  mounted() {
    if (this.defaultYear) {
      this.year = this.defaultYear;
      this.selectedYear = this.defaultYear;
    }
    
    if (this.range) {
      this.setDefaultMonthRange();
      return;
    }

    if (this.defaultMonth) {
      this.selectMonth(this.defaultMonth - 1);
    } else if (!this.noDefault) {
      this.selectMonth(0);
    }
  },
  methods: {
    onChange() {
      if (!Number.parseInt(this.year)) {
        this.year = this.defaultYear || new Date().getFullYear();
      }
      this.$emit("change", this.date);
    },
    selectMonth(index, input = false) {
      if (this.isInactive(index)) {
        return;
      }

      if (this.range) {
        return this.selectMonthRange(index, input);
      }

      const isAlreadySelected = this.currentMonthIndex === index;
      if (this.clearable && isAlreadySelected) {
        this.currentMonthIndex = null;
        this.$emit("clear");
        return;
      }

      if (this.isAlreadySelected) {
        return;
      }

      this.currentMonthIndex = index;
      this.selectedYear = this.year;
      this.onChange();

      if (input) {
        this.$emit("input", this.date);
      }
    },
    selectMonthRange(index, input) {
      if (this.firstRangeMonthIndex === null) {
        this.firstRangeMonthIndex = index;
        return;
      }

      if (
        this.firstRangeMonthIndex !== null &&
        this.secondRangeMonthIndex !== null
      ) {
        this.firstRangeMonthIndex = index;
        this.secondRangeMonthIndex = null;
        return;
      }

      if (index >= this.firstRangeMonthIndex) {
        this.secondRangeMonthIndex = index;
        this.onChange();

        if (input) {
          this.$emit("input", this.date);
        }
        return;
      }

      this.firstRangeMonthIndex = index;
    },
    setDefaultMonthRange() {
      if (
        this.defaultMonthRange === null ||
        this.defaultMonthRange.length !== 2
      ) {
        return;
      }

      const [firstRange, secondRange] = this.defaultMonthRange;

      this.firstRangeMonthIndex = firstRange;
      this.secondRangeMonthIndex = secondRange;
    },
    changeYear(value) {
      this.year += value;

      if (this.isInactive(0)) {
        return;
      }

      this.onChange();
      this.$emit("change-year", this.year);
    },
    isInactive(month) {
      let monthValue = month;
      if (this.minDate === null && this.maxDate === null) {
        return false;
      }

      if (Number.isInteger(monthValue)) {
        monthValue = this.monthsByLang[monthValue];
      }

      const monthKey = this.monthsByLang.indexOf(monthValue) + 1;
      const date = new Date(`${this.year}/${monthKey}/01`);
      const isValidDate = date => date !== null && date instanceof Date;

      if (isValidDate(this.minDate) && date < this.minDate) {
        return true;
      }

      if (isValidDate(this.maxDate) && date > this.maxDate) {
        return true;
      }

      return false;
    }
  }
};
</script>